import { useMutation, useQuery } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { dequal } from "dequal";
import type { History, Transition } from "history";
import {
  type MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type Control,
  Controller,
  type FieldErrors,
  type Path,
  useFieldArray,
  useForm,
  type UseFormGetValues,
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormUnregister,
  useWatch,
} from "react-hook-form";
import { UNSAFE_NavigationContext, useNavigate } from "react-router-dom";

import {
  ActivityCategory,
  ContentType,
  CreateOrUpdateActivityMutation,
  CreateOrUpdateActivityMutationVariables,
  GetLinkableVoiceMessagesQuery,
  GetLinkableVoiceMessagesQueryVariables,
  GetQuestionTypesQuery,
  UpsertDraftActivityMutation,
  UpsertDraftActivityMutationVariables,
} from "@/graphql/graphql";
import useFlag from "@/hooks/useFlag";
import { useSpecialistDropdown } from "@/hooks/useSpecialistDropDown";
import { AppPageNoMaxWidth } from "@/layouts";
import {
  ACTIVITY_CREATE_FORM_ID,
  ActivityCreatePageHeader,
} from "@/pages/ActivityLibrary/components/ActivityCreatePage/ActivityCreatePageHeader";
import { ActivityTypeKeysPicker } from "@/pages/ActivityLibrary/components/ActivityCreatePage/ActivityTypeKeysPicker";
import { SelectExistingActivityForEditModal } from "@/pages/ActivityLibrary/components/ActivityCreatePage/SelectExistingActivityForEditModal";
import {
  CREATE_OR_UPDATE_ACTIVITY_MUTATION,
  GET_QUESTION_TYPES_QUERY,
  UPSERT_DRAFT_ACTIVITY_MUTATION,
} from "@/pages/ActivityLibrary/queries";
import { ACTIVITY_CATEGORIES, ACTIVITY_DIFFICULTIES } from "@/utils/consts";
import FeatureFlags from "@/utils/featureFlags";
import {
  formatEnumLabel,
  formatGqlExecutionErrors,
  graphqlClientErrorMessage,
  rhfErrorMessage,
} from "@/utils/format";

import { GET_LINKABLE_VOICE_MESSAGES_QUERY } from "./ActivityCreatePage.queries";
import * as sx from "./ActivityCreatePage.styles";
import type {
  ActivityBlock,
  ActivityContentBlockProps,
  ActivityCreateFormErrors,
  ActivityCreateSnackbarsProps,
  ActivityFormValues,
  ActivityMetadataCardProps,
  ActivitySubmitAction,
  PendingUnsavedInterrupt,
  QuestionExpectedAnswersProps,
  VoiceMessageOption,
} from "./ActivityCreatePage.types";
import {
  buildCreateActivityInput,
  buildDraftActivityJSON,
  buildFormValuesFromDraftActivityJSON,
  clampCustomResponseTimeMaxSec,
  emptyActivityFormValues,
  emptyBlock,
  LONGEST_RESPONSE_TIME_SEC,
  normalizeActivityFormForReset,
  RESPONSE_TIME_STEP_SEC,
  responseTimeOptions,
  SMALLEST_RESPONSE_TIME_SEC,
  stripReferencesToExistingActivity,
  validateActivityTypeKeys,
  validateCategory,
  validateContents,
  validateContentText,
  validateDifficulty,
  validateQuestionType,
  validateSpecialistId,
  validateTitle,
  withoutInvalidVoiceMessageId,
} from "./ActivityCreatePage.utils";
import { DraftActivitesModal } from "./DraftActivitesModal";

/** MUI outlined labels may not shrink after RHF `reset` / programmatic value updates; tie shrink to the watched value. */
const shouldShrinkOutlinedLabel = (value: unknown) => value != null && String(value).length > 0;

const QuestionExpectedAnswers: React.FC<QuestionExpectedAnswersProps> = ({
  control,
  register,
  getValues,
  setValue,
  blockIndex,
  expectedAnswersError,
}) => {
  const pathName = `contents.${blockIndex}.expectedAnswers` as Path<ActivityFormValues>;
  const answers = (useWatch({ control, name: pathName }) as string[] | undefined) ?? [];

  const addAnswer = () => {
    const current = (getValues(pathName) as string[] | undefined) ?? [];
    setValue(pathName, [...current, ""], { shouldDirty: true });
  };

  const removeAnswer = (answerIndex: number) => {
    const current = (getValues(pathName) as string[] | undefined) ?? [];
    setValue(
      pathName,
      current.filter((_, index) => index !== answerIndex),
      {
        shouldDirty: true,
      }
    );
  };

  return (
    <Box>
      <Typography sx={sx.activityCreateLabelSx}>
        Expected Answer(s) (or examples of answers)
      </Typography>
      <Stack spacing={1}>
        {answers.map((_, answerIndex) => (
          <Stack
            direction="row"
            key={`${blockIndex}-answer-${answerIndex}`}
            spacing={1}
            alignItems="center"
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Expected answer"
              {...register(`contents.${blockIndex}.expectedAnswers.${answerIndex}`)}
            />
            <IconButton
              size="small"
              aria-label="Remove answer"
              onClick={() => removeAnswer(answerIndex)}
              sx={sx.activityCreateExpectedAnswerRemoveIconSx}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
      <Button
        type="button"
        variant="text"
        size="small"
        onClick={addAnswer}
        sx={sx.activityCreateAddAnotherAnswerButtonSx}
      >
        + Add another answer
      </Button>
      {expectedAnswersError && (
        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
          {expectedAnswersError}
        </Typography>
      )}
    </Box>
  );
};

type QuestionBlockFieldProps = {
  blockIndex: number;
  control: Control<ActivityFormValues>;
  getValues: UseFormGetValues<ActivityFormValues>;
  setValue: UseFormSetValue<ActivityFormValues>;
};

type ActivityQuestionBlockSettingsProps = QuestionBlockFieldProps & {
  block: ActivityBlock | undefined;
  blockErrors: NonNullable<FieldErrors<ActivityFormValues>["contents"]>[number] | undefined;
  register: UseFormRegister<ActivityFormValues>;
  unregister: UseFormUnregister<ActivityFormValues>;
  submitActionRef: MutableRefObject<ActivitySubmitAction>;
  sortedQuestionTypes: ActivityContentBlockProps["sortedQuestionTypes"];
  questionTypesLoading: boolean;
  questionTypesHasError: boolean;
};

const MEDIUM_RESPONSE_TIME =
  responseTimeOptions.find((option) => option.key === "MEDIUM") ?? responseTimeOptions[1];

const ResponseTimePresetButtons: React.FC<{
  blockIndex: number;
  preset: ActivityBlock["responsePreset"];
  onPresetChange: (preset: ActivityBlock["responsePreset"]) => void;
  setValue: UseFormSetValue<ActivityFormValues>;
}> = ({ blockIndex, preset, onPresetChange, setValue }) => (
  <Box sx={sx.activityCreateResponsePresetRowSx}>
    {responseTimeOptions.map((option) => (
      <Button
        key={option.key}
        onClick={() => onPresetChange(option.key)}
        sx={sx.activityCreateResponsePresetButtonSx(preset === option.key)}
      >
        {option.label}
      </Button>
    ))}
    <Button
      onClick={() => {
        onPresetChange("CUSTOM");
        setValue(`contents.${blockIndex}.customDurationMax`, MEDIUM_RESPONSE_TIME.max);
        setValue(`contents.${blockIndex}.customExactTime`, false);
      }}
      sx={sx.activityCreateResponsePresetButtonSx(preset === "CUSTOM")}
    >
      CUSTOM
    </Button>
  </Box>
);

const ResponseTimeSection: React.FC<
  Pick<QuestionBlockFieldProps, "blockIndex" | "control" | "setValue">
> = ({ blockIndex, control, setValue }) => (
  <>
    <Typography sx={sx.activityCreateLabelSx}>Response Time</Typography>
    <Controller
      control={control}
      name={`contents.${blockIndex}.responsePreset`}
      render={({ field: { value: preset, onChange: onPresetChange } }) => (
        <>
          <ResponseTimePresetButtons
            preset={preset}
            onPresetChange={onPresetChange}
            blockIndex={blockIndex}
            setValue={setValue}
          />
          {preset === "CUSTOM" && (
            <Box sx={sx.activityCreateCustomDurationSx}>
              {/* Exact time? checkbox */}
              <Controller
                control={control}
                name={`contents.${blockIndex}.customExactTime`}
                render={({ field: { value, onChange } }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!value}
                        onChange={(event) => onChange(event.target.checked)}
                      />
                    }
                    label="Exact time?"
                  />
                )}
              />

              {/* Response time limit input */}
              <Controller
                control={control}
                name={`contents.${blockIndex}.customDurationMax`}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    type="number"
                    size="small"
                    label="Response time"
                    value={value}
                    onChange={onChange}
                    onBlur={(event) => {
                      const parsed = Number(event.target.value);
                      if (!Number.isFinite(parsed)) {
                        return;
                      }
                      onChange(clampCustomResponseTimeMaxSec(parsed));
                    }}
                    inputProps={{
                      min: SMALLEST_RESPONSE_TIME_SEC,
                      max: LONGEST_RESPONSE_TIME_SEC,
                      step: RESPONSE_TIME_STEP_SEC,
                    }}
                    sx={{ width: 160 }}
                  />
                )}
              />
            </Box>
          )}
        </>
      )}
    />
  </>
);

const QuestionScoringSection: React.FC<
  Pick<ActivityQuestionBlockSettingsProps, "blockIndex" | "block" | "control"> &
    Pick<
      ActivityQuestionBlockSettingsProps,
      "blockErrors" | "register" | "getValues" | "setValue" | "unregister" | "submitActionRef"
    >
> = ({
  blockIndex,
  block,
  control,
  register,
  getValues,
  setValue,
  unregister,
  submitActionRef,
  blockErrors,
}) => (
  <Box sx={sx.activityCreateScoringSectionSx}>
    <Controller
      control={control}
      name={`contents.${blockIndex}.isScorable`}
      render={({ field }) => (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={!!field.value}
              onChange={(event) => field.onChange(event.target.checked)}
            />
          }
          label="Should be scored?"
        />
      )}
    />
    {block?.isScorable && (
      <>
        <TextField
          size="small"
          label="Instructions for scoring"
          placeholder="Provide instructions on how to score this question..."
          multiline
          minRows={3}
          fullWidth
          {...register(`contents.${blockIndex}.scoringNotes`)}
          InputLabelProps={{
            shrink: shouldShrinkOutlinedLabel(block?.scoringNotes),
          }}
        />
        <QuestionExpectedAnswers
          control={control}
          register={register}
          getValues={getValues}
          setValue={setValue}
          unregister={unregister}
          blockIndex={blockIndex}
          expectedAnswersError={rhfErrorMessage(blockErrors?.expectedAnswers)}
          submitActionRef={submitActionRef}
          isScorable={!!block?.isScorable}
        />
      </>
    )}
  </Box>
);

const ActivityQuestionBlockSettings: React.FC<ActivityQuestionBlockSettingsProps> = ({
  blockIndex,
  block,
  blockErrors,
  control,
  register,
  getValues,
  setValue,
  unregister,
  submitActionRef,
  sortedQuestionTypes,
  questionTypesLoading,
  questionTypesHasError,
}) => (
  <>
    <Controller
      control={control}
      name={`contents.${blockIndex}.questionType`}
      rules={{
        validate: (value) => validateQuestionType(value, submitActionRef.current),
      }}
      render={({ field }) => (
        <TextField
          select
          size="small"
          label="Question Type"
          name={field.name}
          value={field.value ?? ""}
          onChange={field.onChange}
          onBlur={field.onBlur}
          inputRef={field.ref}
          InputLabelProps={{
            shrink: shouldShrinkOutlinedLabel(field.value),
          }}
          fullWidth
          disabled={
            questionTypesHasError || (questionTypesLoading && sortedQuestionTypes.length === 0)
          }
          error={!!blockErrors?.questionType}
          helperText={blockErrors?.questionType?.message}
        >
          {sortedQuestionTypes.map((qt) => (
            <MenuItem key={qt.questionTypeKey} value={qt.questionTypeKey}>
              {qt.questionTypeLabel}
            </MenuItem>
          ))}
        </TextField>
      )}
    />

    <ResponseTimeSection blockIndex={blockIndex} control={control} setValue={setValue} />

    <QuestionScoringSection
      blockIndex={blockIndex}
      block={block}
      control={control}
      blockErrors={blockErrors}
      register={register}
      getValues={getValues}
      setValue={setValue}
      unregister={unregister}
      submitActionRef={submitActionRef}
    />
  </>
);

const ActivityInsertBetweenRow: React.FC<{
  onInsertInstruction: () => void;
  onInsertQuestion: () => void;
}> = ({ onInsertInstruction, onInsertQuestion }) => (
  <Box sx={sx.activityCreateInsertBetweenRowSx}>
    <Button
      size="small"
      startIcon={<AddIcon sx={sx.activityCreateInsertRowIconSx} />}
      sx={sx.activityCreateInsertInstructionButtonSx}
      onClick={onInsertInstruction}
    >
      Instruction
    </Button>
    <Button
      size="small"
      startIcon={<AddIcon sx={sx.activityCreateInsertRowIconSx} />}
      sx={sx.activityCreateInsertQuestionButtonSx}
      onClick={onInsertQuestion}
    >
      Question
    </Button>
  </Box>
);

const formatVoiceMessageOptionLabel = (voiceMessage: { id: number; title?: string | null }) =>
  `${voiceMessage.title ?? "Untitled"} - ${voiceMessage.id}`;

const voiceMessageFilterOptions = createFilterOptions<VoiceMessageOption>({
  stringify: (option) => formatVoiceMessageOptionLabel(option),
});

const ActivityMetadataCard: React.FC<ActivityMetadataCardProps> = ({
  control,
  register,
  errors,
  titleWatch,
  descriptionWatch,
  specialistIdWatch,
  specialistOptions,
  specialistsLoading,
  category,
  submitActionRef,
  showVoiceMessageLink,
  voiceMessageOptions,
  voiceMessagesLoading,
}) => {
  const activityTypeKeysMessage = rhfErrorMessage(errors.activityTypeKeys);

  return (
    <Box sx={sx.activityCreateCardSx}>
      <Typography sx={sx.activityCreateSectionTitleSx}>Activity metadata</Typography>
      <Stack spacing={1.5}>
        <TextField
          size="small"
          label="Title"
          {...register("title", {
            validate: (value) => validateTitle(value, submitActionRef.current),
          })}
          InputLabelProps={{ shrink: shouldShrinkOutlinedLabel(titleWatch) }}
          error={!!errors.title}
          helperText={errors.title?.message}
          fullWidth
        />

        <Box>
          <Typography sx={sx.activityCreateLabelSx}>Author</Typography>
          <Controller
            control={control}
            name="specialistId"
            rules={{
              validate: (value) => validateSpecialistId(value, submitActionRef.current),
            }}
            render={({ field: { value, onChange } }) => (
              <TextField
                select
                size="small"
                sx={sx.activityCreateSpecialistSelectSx}
                value={value ?? ""}
                onChange={(event) => {
                  const next = event.target.value;
                  onChange(next === "" ? null : Number(next));
                }}
                disabled={specialistsLoading}
                InputLabelProps={{ shrink: shouldShrinkOutlinedLabel(specialistIdWatch) }}
                error={!!errors.specialistId}
                helperText={errors.specialistId?.message}
              >
                {specialistOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Box>

        <Box>
          <Typography sx={sx.activityCreateLabelSx}>Category</Typography>
          <Controller
            control={control}
            name="category"
            rules={{ validate: (value) => validateCategory(value, submitActionRef.current) }}
            render={({ field: { value, onChange } }) => (
              <Box sx={sx.activityCreateFullWidthToggleRowSx}>
                {ACTIVITY_CATEGORIES.map((categoryOption) => {
                  const styles = sx.categoryToggleStyles[categoryOption];
                  const active = value === categoryOption;
                  return (
                    <Button
                      key={categoryOption}
                      onClick={() => onChange(categoryOption)}
                      sx={sx.activityCreateFullWidthToggleButtonSx(
                        active,
                        styles.active,
                        styles.inactive
                      )}
                    >
                      {formatEnumLabel(categoryOption)}
                    </Button>
                  );
                })}
              </Box>
            )}
          />
          {errors.category?.message && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
              {errors.category.message}
            </Typography>
          )}
        </Box>

        {category === ActivityCategory.CognitiveExercise && (
          <Box>
            <Typography sx={sx.activityCreateLabelSx}>Activity types</Typography>
            <Controller
              control={control}
              name="activityTypeKeys"
              rules={{
                validate: (value) =>
                  validateActivityTypeKeys(category, value, submitActionRef.current),
              }}
              render={({ field: { value, onChange } }) => (
                <ActivityTypeKeysPicker value={value} onChange={onChange} />
              )}
            />
            {activityTypeKeysMessage && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                {activityTypeKeysMessage}
              </Typography>
            )}
          </Box>
        )}

        <Box>
          <Typography sx={sx.activityCreateLabelSx}>Difficulty</Typography>
          <Controller
            control={control}
            name="difficulty"
            rules={{ validate: (value) => validateDifficulty(value, submitActionRef.current) }}
            render={({ field: { value, onChange } }) => (
              <Box sx={sx.activityCreateFullWidthToggleRowSx}>
                {ACTIVITY_DIFFICULTIES.map((difficultyOption) => {
                  const styles = sx.difficultyToggleStyles[difficultyOption];
                  const active = value === difficultyOption;
                  return (
                    <Button
                      key={difficultyOption}
                      onClick={() => onChange(difficultyOption)}
                      sx={sx.activityCreateFullWidthToggleButtonSx(
                        active,
                        styles.active,
                        styles.inactive
                      )}
                    >
                      {formatEnumLabel(difficultyOption)}
                    </Button>
                  );
                })}
              </Box>
            )}
          />
          {errors.difficulty?.message && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
              {errors.difficulty.message}
            </Typography>
          )}
        </Box>

        {showVoiceMessageLink && (
          <Box>
            <Typography sx={sx.activityCreateLabelSx}>Voice message (optional)</Typography>
            <Controller
              control={control}
              name="voiceMessageId"
              render={({ field: { value, onChange } }) => (
                <Autocomplete
                  options={voiceMessageOptions}
                  loading={voiceMessagesLoading}
                  value={voiceMessageOptions.find((vm) => vm.id === value) ?? null}
                  onChange={(_, newValue) => onChange(newValue?.id ?? null)}
                  getOptionLabel={formatVoiceMessageOptionLabel}
                  filterOptions={voiceMessageFilterOptions}
                  isOptionEqualToValue={(option, selected) => option.id === selected.id}
                  openOnFocus
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Search by title or ID"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {voiceMessagesLoading ? (
                              <CircularProgress color="inherit" size={16} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  fullWidth
                />
              )}
            />
          </Box>
        )}

        <TextField
          size="small"
          label="Additional description"
          {...register("description")}
          InputLabelProps={{ shrink: shouldShrinkOutlinedLabel(descriptionWatch) }}
          multiline
          minRows={2}
          fullWidth
        />
      </Stack>
    </Box>
  );
};

const ActivityContentBlock: React.FC<ActivityContentBlockProps> = ({
  index,
  block,
  expanded,
  isFirst,
  isLast,
  onToggleCollapsed,
  onMoveUp,
  onMoveDown,
  onRemove,
  onExpandIconClick,
  control,
  register,
  getValues,
  setValue,
  unregister,
  submitActionRef,
  formErrors,
  sortedQuestionTypes,
  questionTypesLoading,
  questionTypesHasError,
}) => {
  const blockErrors = formErrors.contents?.[index];

  return (
    <Box sx={sx.activityCreateBlockCardSx(block?.contentType)}>
      <Box
        sx={sx.activityCreateBlockHeaderSx}
        onClick={onToggleCollapsed}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggleCollapsed();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={sx.activityCreateBlockHeaderTitleStackSx}
        >
          <Typography variant="subtitle2" component="span" noWrap>
            {block?.contentType === "QUESTION" ? "Question" : "Instruction"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={sx.activityCreateBlockHeaderPreviewTextSx}
          >
            {block?.text ? block.text.slice(0, 50) : "No content yet"}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <IconButton size="small" disabled={isFirst} onClick={onMoveUp}>
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" disabled={isLast} onClick={onMoveDown}>
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onRemove}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={expanded ? "Collapse block" : "Expand block"}
            onClick={onExpandIconClick}
          >
            {expanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
          </IconButton>
        </Stack>
      </Box>

      <Box sx={expanded ? sx.activityCreateBlockBodyInnerSx : sx.activityCreateBlockBodyHiddenSx}>
        <TextField
          size="small"
          label={block?.contentType === "QUESTION" ? "Question text" : "Instructions text"}
          multiline
          minRows={3}
          fullWidth
          {...register(`contents.${index}.text`, {
            validate: (value) => validateContentText(value, submitActionRef.current),
          })}
          InputLabelProps={{ shrink: shouldShrinkOutlinedLabel(block?.text) }}
          error={!!blockErrors?.text}
          helperText={blockErrors?.text?.message}
        />

        {block?.contentType === "QUESTION" && (
          <ActivityQuestionBlockSettings
            blockIndex={index}
            block={block}
            blockErrors={blockErrors}
            control={control}
            register={register}
            getValues={getValues}
            setValue={setValue}
            unregister={unregister}
            submitActionRef={submitActionRef}
            sortedQuestionTypes={sortedQuestionTypes}
            questionTypesLoading={questionTypesLoading}
            questionTypesHasError={questionTypesHasError}
          />
        )}
      </Box>
    </Box>
  );
};

const ActivityEmptyContentPlaceholder: React.FC = () => (
  <Box sx={sx.activityCreateEmptyContentPlaceholderSx}>
    <Typography variant="body2">No content sections yet</Typography>
    <Typography variant="caption" display="block" sx={sx.activityCreateEmptyContentCaptionSx}>
      Use the buttons above to add content sections
    </Typography>
  </Box>
);

const ActivityFormActionsFooter: React.FC<{
  onReset: () => void;
  onSaveDraftClick: () => void;
  onSaveDraftAsNewClick: () => void;
  onSaveAndPublishClick: () => void;
}> = ({ onReset, onSaveDraftClick, onSaveDraftAsNewClick, onSaveAndPublishClick }) => (
  <Stack direction="row" spacing={1} justifyContent="flex-end" pb={2}>
    <Button variant="outlined" sx={sx.activityCreateResetFormButtonSx} onClick={onReset}>
      Reset form
    </Button>
    <Button
      type="submit"
      variant="contained"
      sx={sx.activityCreatePrimaryButtonSx}
      onClick={onSaveDraftClick}
    >
      Save draft
    </Button>
    <Button
      type="submit"
      variant="contained"
      sx={sx.activityCreatePrimaryButtonSx}
      onClick={onSaveDraftAsNewClick}
    >
      Save draft as new
    </Button>
    <Button
      type="submit"
      variant="contained"
      sx={sx.activityCreatePrimaryButtonSx}
      onClick={onSaveAndPublishClick}
    >
      Save and publish
    </Button>
  </Stack>
);

const UnsavedChangesDialog: React.FC<{
  open: boolean;
  isSaving: boolean;
  onClose: NonNullable<React.ComponentProps<typeof Dialog>["onClose"]>;
  onSaveDraft: () => void;
  onDiscard: () => void;
}> = ({ open, isSaving, onClose, onSaveDraft, onDiscard }) => {
  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableEscapeKeyDown={isSaving}>
      <DialogTitle>Unsaved changes</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          You have unsaved changes, do you want to save your work?
        </Typography>
      </DialogContent>
      <DialogActions sx={sx.dialogActionsSx}>
        <Button variant="outlined" onClick={onDiscard} disabled={isSaving}>
          No
        </Button>
        <Button variant="contained" onClick={onSaveDraft} disabled={isSaving}>
          Save draft
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ActivityCreateSnackbars: React.FC<ActivityCreateSnackbarsProps> = ({
  showError,
  errorNotice,
  onDismissError,
  onErrorSnackbarExited,
  showSuccess,
  successNotice,
  onDismissSuccess,
}) => {
  if (!showError && !showSuccess) {
    return null;
  }

  return (
    <>
      {showError && (
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={showError}
          autoHideDuration={5000}
          onClose={onDismissError}
          TransitionProps={{ onExited: onErrorSnackbarExited }}
        >
          <Alert severity="error" onClose={onDismissError}>
            {errorNotice ?? "Could not save draft."}
          </Alert>
        </Snackbar>
      )}
      {showSuccess && (
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={showSuccess}
          autoHideDuration={4000}
          onClose={onDismissSuccess}
        >
          <Alert severity="success" onClose={onDismissSuccess}>
            {successNotice ?? "Draft saved."}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export const ActivityCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const navigationContext = useContext(UNSAFE_NavigationContext);
  const showVoiceMessageLink = useFlag(FeatureFlags.LINK_ACTIVITY_VOICE_MESSAGE);

  const [upsertDraftActivity, { error: upsertDraftActivityError }] = useMutation<
    UpsertDraftActivityMutation,
    UpsertDraftActivityMutationVariables
  >(UPSERT_DRAFT_ACTIVITY_MUTATION);

  const [createActivity] = useMutation<
    CreateOrUpdateActivityMutation,
    CreateOrUpdateActivityMutationVariables
  >(CREATE_OR_UPDATE_ACTIVITY_MUTATION, {
    refetchQueries: [
      "GetActivity", // TODO: Just update the specific activity affected
      "GetActivities", // TODO: Just update the specific activity affected
      "GetDraftActivities", // TODO: Just remove the temp/intermediate draft
      "GetLinkableVoiceMessages", // TODO: Just remove the (now linked) voice message from list of linkable VMs
    ],
  });

  const {
    data: questionTypesData,
    loading: questionTypesLoading,
    error: questionTypesError,
  } = useQuery<GetQuestionTypesQuery>(GET_QUESTION_TYPES_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const { data: voiceMessagesData, loading: voiceMessagesLoading } = useQuery<
    GetLinkableVoiceMessagesQuery,
    GetLinkableVoiceMessagesQueryVariables
  >(GET_LINKABLE_VOICE_MESSAGES_QUERY, {
    fetchPolicy: "cache-and-network",
    skip: !showVoiceMessageLink,
    variables: { data: { hasLinkedActivity: false } },
  });

  const voiceMessageOptions = useMemo(
    () => voiceMessagesData?.voiceMessages?.data ?? [],
    [voiceMessagesData?.voiceMessages?.data]
  );

  const linkableVoiceMessageIds = useMemo(
    () => voiceMessageOptions.map((vm) => vm.id),
    [voiceMessageOptions]
  );

  const sortedQuestionTypes = useMemo(() => {
    const types = questionTypesData?.questionTypes ?? [];
    return [...types].sort((a, b) => a.questionTypeLabel.localeCompare(b.questionTypeLabel));
  }, [questionTypesData?.questionTypes]);

  const { options: specialistOptions, loading: specialistsLoading } = useSpecialistDropdown();

  const defaultFormValues = useMemo(() => emptyActivityFormValues(), []);
  const submitActionRef = useRef<ActivitySubmitAction>("draft");

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    unregister,
    formState: { errors },
  } = useForm<ActivityFormValues>({
    mode: "onSubmit",
    defaultValues: defaultFormValues,
    // Content block fields unmount when collapsed; keep values for save/load round-trips.
    shouldUnregister: false,
  });
  const [dirtyBaseline, setDirtyBaseline] = useState<ActivityFormValues>(() => defaultFormValues);
  const watchedValues = useWatch({ control });
  // Can't rely on `isDirty` because it's not updated immediately after a value change.
  const hasUnsavedChanges = !dequal(watchedValues ?? dirtyBaseline, dirtyBaseline);

  const resetWithBaseline = useCallback(
    (next: ActivityFormValues) => {
      const normalized = normalizeActivityFormForReset(next);
      reset(normalized);
      setDirtyBaseline(normalized);
    },
    [reset]
  );

  const formValuesWithResolvableVoiceMessageId = useCallback(
    (data: ActivityFormValues): ActivityFormValues => {
      if (!showVoiceMessageLink || voiceMessagesLoading) {
        return data;
      }

      return withoutInvalidVoiceMessageId(data, linkableVoiceMessageIds);
    },
    [linkableVoiceMessageIds, showVoiceMessageLink, voiceMessagesLoading]
  );

  const clearStaleVoiceMessageIdFromForm = useCallback(
    (data: ActivityFormValues) => {
      const resolved = formValuesWithResolvableVoiceMessageId(data);
      if (resolved.voiceMessageId === data.voiceMessageId) {
        return resolved;
      }

      setValue("voiceMessageId", null);
      setDirtyBaseline((baseline) => ({ ...baseline, voiceMessageId: null }));
      return resolved;
    },
    [formValuesWithResolvableVoiceMessageId, setValue]
  );

  const { fields, remove, move, insert } = useFieldArray({
    control,
    name: "contents",
    rules: {
      validate: (value) => validateContents(value, submitActionRef.current),
    },
  });
  const [collapsedBlocks, setCollapsedBlocks] = useState<string[]>([]);
  const [showError, setShowError] = useState(false);
  const [errorNotice, setErrorNotice] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);
  const [isDraftsModalOpen, setIsDraftsModalOpen] = useState(false);
  const [isEditExistingModalOpen, setIsEditExistingModalOpen] = useState(false);
  const [draftActivityId, setDraftActivityId] = useState<number | undefined>();
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);
  const [interruptSavePending, setInterruptSavePending] = useState(false);
  const errorSnackbarUserDismissedRef = useRef(false);
  const pendingInterruptRef = useRef<PendingUnsavedInterrupt | null>(null);
  /** `history.block` unsubscribe - must run before `tx.retry()` or the retry is blocked again. */
  const navigateBlockUnsubscribeRef = useRef<(() => void) | null>(null);

  const category = useWatch({ control, name: "category" });
  const contents = useWatch({ control, name: "contents" }) ?? [];
  const titleWatch = useWatch({ control, name: "title" });
  const descriptionWatch = useWatch({ control, name: "description" });
  const specialistIdWatch = useWatch({ control, name: "specialistId" });
  const voiceMessageIdWatch = useWatch({ control, name: "voiceMessageId" });

  useEffect(() => {
    if (!showVoiceMessageLink || voiceMessagesLoading || voiceMessageIdWatch == null) {
      return;
    }
    if (linkableVoiceMessageIds.includes(voiceMessageIdWatch)) {
      return;
    }

    setValue("voiceMessageId", null);
    setDirtyBaseline((baseline) => ({ ...baseline, voiceMessageId: null }));
  }, [
    linkableVoiceMessageIds,
    showVoiceMessageLink,
    setValue,
    voiceMessageIdWatch,
    voiceMessagesLoading,
  ]);

  useEffect(() => {
    if (specialistsLoading || specialistOptions.length === 0 || specialistIdWatch != null) {
      return;
    }
    const encodedEmail = localStorage.getItem("username") || "";
    const currentUserEmail = encodedEmail ? atob(encodedEmail) : "";
    const matchedSpecialist = specialistOptions.find(
      (specialist) => specialist.email === currentUserEmail
    );
    if (matchedSpecialist) {
      setValue("specialistId", matchedSpecialist.value);
    }
  }, [specialistsLoading, specialistOptions, specialistIdWatch, setValue]);

  const toggleCollapsed = useCallback(
    (fieldId: string) => {
      setCollapsedBlocks((current) =>
        current.includes(fieldId) ? current.filter((id) => id !== fieldId) : [...current, fieldId]
      );
    },
    [setCollapsedBlocks]
  );

  const reportDraftSaveError = useCallback((notice: string) => {
    errorSnackbarUserDismissedRef.current = false;
    setErrorNotice(notice);
    setShowError(true);
  }, []);

  const reportDraftSaveSuccess = useCallback((notice: string) => {
    setShowError(false);
    setErrorNotice(null);
    setSuccessNotice(notice);
    setShowSuccess(true);
  }, []);

  const persistDraft = useCallback(
    async (formValues: ActivityFormValues): Promise<ActivityFormValues | false> => {
      if (formValues.specialistId == null) {
        reportDraftSaveError("Select a specialist before saving the draft.");
        return false;
      }
      const resolvedData = clearStaleVoiceMessageIdFromForm(formValues);
      try {
        const result = await upsertDraftActivity({
          variables: {
            activityJSON: buildDraftActivityJSON(resolvedData),
            id: draftActivityId,
          },
        });
        const gqlErrMsg = formatGqlExecutionErrors(result);
        if (gqlErrMsg) {
          reportDraftSaveError(gqlErrMsg);
          return false;
        }
        if (!result.data?.upsertDraftActivity) {
          reportDraftSaveError("Could not save draft.");
          return false;
        }
        setDraftActivityId(result.data.upsertDraftActivity.id);
        return formValuesWithResolvableVoiceMessageId(
          buildFormValuesFromDraftActivityJSON(result.data.upsertDraftActivity.activityJSON)
        );
      } catch (error) {
        reportDraftSaveError(graphqlClientErrorMessage(error, "Could not save draft."));
        return false;
      }
    },
    [
      clearStaleVoiceMessageIdFromForm,
      draftActivityId,
      formValuesWithResolvableVoiceMessageId,
      reportDraftSaveError,
      upsertDraftActivity,
    ]
  );

  const persistPublish = useCallback(
    async (data: ActivityFormValues) => {
      if (data.specialistId == null) {
        reportDraftSaveError("Select a specialist before publishing.");
        return false;
      }
      const resolvedData = clearStaleVoiceMessageIdFromForm(data);
      try {
        const result = await createActivity({
          variables: {
            input: buildCreateActivityInput(resolvedData),
            draftId: draftActivityId,
          },
        });
        const gqlErrMsg = formatGqlExecutionErrors(result);
        if (gqlErrMsg) {
          reportDraftSaveError(gqlErrMsg);
          return false;
        }
        if (!result.data?.createOrUpdateActivity) {
          reportDraftSaveError("Could not publish activity.");
          return false;
        }
        return true;
      } catch (error) {
        reportDraftSaveError(graphqlClientErrorMessage(error, "Could not publish activity."));
        return false;
      }
    },
    [clearStaleVoiceMessageIdFromForm, createActivity, draftActivityId, reportDraftSaveError]
  );

  const applyDraftFromPicker = useCallback(
    (draft: { id: number; activityJSON: unknown }) => {
      const loaded = formValuesWithResolvableVoiceMessageId(
        buildFormValuesFromDraftActivityJSON(draft.activityJSON)
      );
      resetWithBaseline(loaded);
      setCollapsedBlocks([]);
      setDraftActivityId(draft.id);
      setIsDraftsModalOpen(false);
      setIsEditExistingModalOpen(false);
      reportDraftSaveSuccess("Draft loaded successfully.");
    },
    [formValuesWithResolvableVoiceMessageId, reportDraftSaveSuccess, resetWithBaseline]
  );

  useEffect(() => {
    if (!hasUnsavedChanges) {
      navigateBlockUnsubscribeRef.current = null;
      return;
    }
    const history = navigationContext?.navigator as History | undefined;
    if (!history?.block) {
      return;
    }
    const unblock = history.block((tx) => {
      pendingInterruptRef.current = { kind: "navigate", tx };
      setUnsavedDialogOpen(true);
    });
    navigateBlockUnsubscribeRef.current = unblock;
    return () => {
      navigateBlockUnsubscribeRef.current?.();
      navigateBlockUnsubscribeRef.current = null;
    };
  }, [hasUnsavedChanges, navigationContext]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!upsertDraftActivityError) {
      errorSnackbarUserDismissedRef.current = false;
      return;
    }

    if (errorSnackbarUserDismissedRef.current) {
      return;
    }

    setErrorNotice(graphqlClientErrorMessage(upsertDraftActivityError, "Could not save draft."));
    setShowError(true);
  }, [upsertDraftActivityError]);

  const dismissErrorSnackbar = useCallback(() => {
    errorSnackbarUserDismissedRef.current = true;
    setShowError(false);
  }, [setShowError]);

  const dismissSuccessSnackbar = useCallback(() => {
    setShowSuccess(false);
    setSuccessNotice(null);
  }, [setShowSuccess, setSuccessNotice]);

  const releaseHistoryBlockAndRetryNavigation = useCallback((blockedTransition: Transition) => {
    const unblock = navigateBlockUnsubscribeRef.current;
    navigateBlockUnsubscribeRef.current = null;
    unblock?.();
    blockedTransition.retry();
  }, []);

  const finishInterruptDiscard = useCallback(() => {
    const pending = pendingInterruptRef.current;
    pendingInterruptRef.current = null;
    setUnsavedDialogOpen(false);
    if (!pending) {
      return;
    }
    if (pending.kind === "navigate") {
      releaseHistoryBlockAndRetryNavigation(pending.tx);
      return;
    }
    if (pending.kind === "reset") {
      resetWithBaseline(emptyActivityFormValues());
      setDraftActivityId(undefined);
      setCollapsedBlocks([]);
      return;
    }
    applyDraftFromPicker(pending.draft);
  }, [applyDraftFromPicker, releaseHistoryBlockAndRetryNavigation, resetWithBaseline]);

  const requestResetForm = useCallback(() => {
    if (!hasUnsavedChanges) {
      resetWithBaseline(emptyActivityFormValues());
      setDraftActivityId(undefined);
      setCollapsedBlocks([]);
      return;
    }
    pendingInterruptRef.current = { kind: "reset" };
    setUnsavedDialogOpen(true);
  }, [hasUnsavedChanges, resetWithBaseline]);

  const requestLoadDraft = useCallback(
    (draft: { id: number; title: string; activityJSON: unknown }) => {
      if (draft.id === draftActivityId) {
        setIsDraftsModalOpen(false);
        setIsEditExistingModalOpen(false);
        return;
      }
      if (!hasUnsavedChanges) {
        applyDraftFromPicker(draft);
        return;
      }
      pendingInterruptRef.current = { kind: "loadDraft", draft };
      setUnsavedDialogOpen(true);
    },
    [draftActivityId, hasUnsavedChanges, applyDraftFromPicker]
  );

  const onUnsavedDialogClose: NonNullable<React.ComponentProps<typeof Dialog>["onClose"]> =
    useCallback(
      (_event, reason) => {
        if (interruptSavePending) {
          return;
        }
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          pendingInterruptRef.current = null;
          setUnsavedDialogOpen(false);
        }
      },
      [interruptSavePending, setUnsavedDialogOpen]
    );

  const onUnsavedDialogSaveDraft = useCallback(() => {
    handleSubmit(async (data) => {
      setInterruptSavePending(true);
      try {
        const savedForm = await persistDraft(data);
        if (!savedForm) {
          return;
        }
        const pending = pendingInterruptRef.current;
        pendingInterruptRef.current = null;
        setUnsavedDialogOpen(false);
        if (!pending) {
          resetWithBaseline(savedForm);
          reportDraftSaveSuccess("Draft saved successfully.");
          return;
        }
        if (pending.kind === "navigate") {
          resetWithBaseline(savedForm);
          releaseHistoryBlockAndRetryNavigation(pending.tx);
          reportDraftSaveSuccess("Draft saved successfully.");
          return;
        }
        if (pending.kind === "reset") {
          resetWithBaseline(emptyActivityFormValues());
          setDraftActivityId(undefined);
          setCollapsedBlocks([]);
          reportDraftSaveSuccess("Draft saved successfully.");
          return;
        }
        resetWithBaseline(
          formValuesWithResolvableVoiceMessageId(
            buildFormValuesFromDraftActivityJSON(pending.draft.activityJSON)
          )
        );
        setCollapsedBlocks([]);
        setDraftActivityId(pending.draft.id);
        setIsDraftsModalOpen(false);
        reportDraftSaveSuccess("Draft loaded successfully.");
      } finally {
        setInterruptSavePending(false);
      }
    })();
  }, [
    formValuesWithResolvableVoiceMessageId,
    handleSubmit,
    persistDraft,
    releaseHistoryBlockAndRetryNavigation,
    reportDraftSaveSuccess,
    resetWithBaseline,
  ]);

  const onSaveDraftClick = useCallback(() => {
    submitActionRef.current = "draft";
  }, []);

  const onSaveDraftAsNewClick = useCallback(() => {
    submitActionRef.current = "draft-as-new";
  }, []);

  const onSaveAndPublishClick = useCallback(() => {
    submitActionRef.current = "publish";
  }, []);

  const onSubmitPublish = useCallback(
    async (data: ActivityFormValues) => {
      const published = await persistPublish(data);
      if (!published) {
        return;
      }
      resetWithBaseline(emptyActivityFormValues());
      setCollapsedBlocks([]);
      setDraftActivityId(undefined);
      // Disable navigation blocking before navigating
      navigateBlockUnsubscribeRef.current?.();
      navigateBlockUnsubscribeRef.current = null;
      navigate("/activities", {
        state: { activityPublishedMessage: `Published "${data.title.trim()}".` },
      });
    },
    [persistPublish, resetWithBaseline, navigate]
  );

  const onSubmit = useCallback(
    async (data: ActivityFormValues) => {
      if (submitActionRef.current === "publish") {
        return onSubmitPublish(data);
      }
      const dataForSubmit =
        submitActionRef.current === "draft-as-new" ? stripReferencesToExistingActivity(data) : data;
      const savedForm = await persistDraft(dataForSubmit);
      if (!savedForm) {
        return;
      }
      resetWithBaseline(savedForm);
      reportDraftSaveSuccess("Draft saved successfully.");
    },
    [persistDraft, reportDraftSaveSuccess, onSubmitPublish, resetWithBaseline]
  );

  return (
    <AppPageNoMaxWidth>
      <Box sx={sx.activityCreatePageWrapSx}>
        <ActivityCreatePageHeader
          onBackToActivities={() => navigate("/activities")}
          onOpenLoadDraft={() => setIsDraftsModalOpen(true)}
          onOpenEditExisting={() => setIsEditExistingModalOpen(true)}
          onSaveDraftClick={onSaveDraftClick}
          onSaveDraftAsNewClick={onSaveDraftAsNewClick}
          onSaveAndPublishClick={onSaveAndPublishClick}
        />

        <Box sx={sx.activityCreateContentSx}>
          <Box
            id={ACTIVITY_CREATE_FORM_ID}
            component="form"
            sx={sx.activityCreateFormSx}
            onSubmit={handleSubmit(onSubmit)}
          >
            <ActivityMetadataCard
              control={control}
              register={register}
              errors={errors}
              titleWatch={titleWatch}
              descriptionWatch={descriptionWatch}
              specialistIdWatch={specialistIdWatch}
              specialistOptions={specialistOptions}
              specialistsLoading={specialistsLoading}
              category={category}
              submitActionRef={submitActionRef}
              showVoiceMessageLink={showVoiceMessageLink}
              voiceMessageOptions={voiceMessageOptions}
              voiceMessagesLoading={voiceMessagesLoading}
            />

            <Box sx={sx.activityCreateCardSx}>
              <Typography sx={sx.activityCreateContentSectionTitleSx}>Content</Typography>

              {(errors as ActivityCreateFormErrors).contents?.root?.message && (
                <Alert severity="error" sx={sx.activityCreateAlertInlineSx}>
                  {(errors as ActivityCreateFormErrors).contents?.root?.message}
                </Alert>
              )}

              {questionTypesError && (
                <Alert severity="error" sx={sx.activityCreateAlertInlineSx}>
                  Could not load question types. Try again later.
                </Alert>
              )}

              <ActivityInsertBetweenRow
                onInsertInstruction={() => insert(0, emptyBlock(ContentType.Instruction))}
                onInsertQuestion={() => insert(0, emptyBlock(ContentType.Question))}
              />

              <Stack spacing={0}>
                {fields.map((field, index) => {
                  const block = contents[index];
                  const collapsed = collapsedBlocks.includes(field.id);
                  const expanded = !collapsed;
                  const isFirst = index === 0;
                  const isLast = index === fields.length - 1;

                  return (
                    <Box key={field.id}>
                      <ActivityContentBlock
                        index={index}
                        block={block}
                        expanded={expanded}
                        isFirst={isFirst}
                        isLast={isLast}
                        onToggleCollapsed={() => toggleCollapsed(field.id)}
                        onMoveUp={(event) => {
                          event.stopPropagation();
                          move(index, index - 1);
                        }}
                        onMoveDown={(event) => {
                          event.stopPropagation();
                          move(index, index + 1);
                        }}
                        onRemove={(event) => {
                          event.stopPropagation();
                          remove(index);
                        }}
                        onExpandIconClick={(event) => {
                          event.stopPropagation();
                          toggleCollapsed(field.id);
                        }}
                        control={control}
                        register={register}
                        getValues={getValues}
                        setValue={setValue}
                        unregister={unregister}
                        submitActionRef={submitActionRef}
                        formErrors={errors}
                        sortedQuestionTypes={sortedQuestionTypes}
                        questionTypesLoading={questionTypesLoading}
                        questionTypesHasError={!!questionTypesError}
                      />

                      <ActivityInsertBetweenRow
                        onInsertInstruction={() =>
                          insert(index + 1, emptyBlock(ContentType.Instruction))
                        }
                        onInsertQuestion={() => insert(index + 1, emptyBlock(ContentType.Question))}
                      />
                    </Box>
                  );
                })}

                {!fields.length && <ActivityEmptyContentPlaceholder />}
              </Stack>
            </Box>

            <ActivityFormActionsFooter
              onReset={requestResetForm}
              onSaveDraftClick={onSaveDraftClick}
              onSaveDraftAsNewClick={onSaveDraftAsNewClick}
              onSaveAndPublishClick={onSaveAndPublishClick}
            />
          </Box>
        </Box>
      </Box>
      {isEditExistingModalOpen && (
        <SelectExistingActivityForEditModal
          open={isEditExistingModalOpen}
          onClose={() => setIsEditExistingModalOpen(false)}
          onDraftForEdit={requestLoadDraft}
        />
      )}
      {isDraftsModalOpen && (
        <DraftActivitesModal
          open={isDraftsModalOpen}
          onClose={() => setIsDraftsModalOpen(false)}
          onLoadDraft={requestLoadDraft}
        />
      )}
      {unsavedDialogOpen && (
        <UnsavedChangesDialog
          open={unsavedDialogOpen}
          isSaving={interruptSavePending}
          onClose={onUnsavedDialogClose}
          onSaveDraft={onUnsavedDialogSaveDraft}
          onDiscard={finishInterruptDiscard}
        />
      )}
      {(showError || showSuccess) && (
        <ActivityCreateSnackbars
          showError={showError}
          errorNotice={errorNotice}
          onDismissError={dismissErrorSnackbar}
          onErrorSnackbarExited={() => setErrorNotice(null)}
          showSuccess={showSuccess}
          successNotice={successNotice}
          onDismissSuccess={dismissSuccessSnackbar}
        />
      )}
    </AppPageNoMaxWidth>
  );
};
